import { Form } from "@client/content/controls";
import { getRootContainerProps, getRootContainerPropsDep } from "@client/content/lib/helper";
import { ModuleButton } from "@client/content/modules/Button";
import { type ModuleApiInfo } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import Circle from "@client/controls/Circle/component";
import { type FormButtonProps, type FormFieldInfo } from "@client/controls/Form/component";
import RichEditor from "@client/controls/Inputs/RichEditor/component";
import Select from "@client/controls/Inputs/Select/component";
import TextInput from "@client/controls/Inputs/TextInput/component";
import Modal from "@client/controls/Modal/container";
import { Status } from "@client/types";
import { useI18n } from "@common/hooks/i18n";
import * as React from "react";
import { type Forums } from "../types";

export type MapProps = RootContainerProps & {
  apiInfo: ModuleApiInfo | null;
  forumOptions: Forums.Api.ForumOptionItem[];
  item: Forums.Post | null;

  isVisible: boolean;
  status: string;
};

export type ActionProps = {
  onSubmit: (data: Forums.View.AddEditPost) => void;
  onClose: () => void;
};

type ContentProps = RootContainerProps & {
  apiInfo: ModuleApiInfo;
  forumOptions: Forums.Api.ForumOptionItem[];
  item: Forums.Post | null;
  status: string;

  onSubmit: (data: Forums.View.AddEditPost) => void;
};
const Content = (props: ContentProps) => {
  const i18n = useI18n();

  const fields = React.useMemo(() => {
    let fields: FormFieldInfo[] = [
      {
        name: "forumId",
        label: i18n.forum,
        Component: Select,
        componentOpts: {
          options: props.forumOptions.map((x) => ({ ...x, image: <Circle color={x.color} size={8} /> })),
          sort: "asc"
        },
        isRequired: true,
        defaultValue: props.item?.forumId
      },
      {
        name: "subject",
        label: i18n.title,
        Component: TextInput,
        isRequired: true,
        defaultValue: props.item?.subject
      },
      {
        name: "message",
        label: i18n.content,
        Component: RichEditor,
        componentOpts: { toolbar: "default" },
        isRequired: true,
        defaultValue: props.item?.message
      }
    ];

    return fields;
  }, [props.forumOptions, props.item]);

  const SubmitButton = React.useMemo(
    () => (fbp: FormButtonProps) =>
      (
        <ModuleButton
          {...getRootContainerProps(props)}
          isDisabled={props.status === Status.Saving}
          onClick={fbp.ifValidated(props.onSubmit)}
        >
          {i18n.save}
        </ModuleButton>
      ),
    [...getRootContainerPropsDep(props), props.onSubmit, props.status]
  );

  return <Form {...getRootContainerProps(props)} fields={fields} Button={SubmitButton} />;
};

export type Props = ActionProps & MapProps;
export default (props: Props) => {
  const i18n = useI18n();

  return (
    <Modal
      className=""
      hasBackdrop
      isAdminColor={false}
      isDraggable={false}
      isCentered
      isLoading={props.status === Status.Loading}
      isVisible={props.isVisible}
      closeBehaviour="cross"
      onClose={props.onClose}
      width={800}
    >
      <Modal.Header title={props.item ? i18n.updatePostSettings : i18n.createPost} />
      <Modal.Body>
        {props.apiInfo && (
          <Content
            {...getRootContainerProps(props)}
            status={props.status}
            apiInfo={props.apiInfo}
            id={props.id}
            item={props.item}
            forumOptions={props.forumOptions}
            onSubmit={props.onSubmit}
          />
        )}
      </Modal.Body>
    </Modal>
  );
};
